// Utility to sync Spotify tokens with the main backend API
import { createApiClient } from '@applauss/api-client';
import {
  saveApplaussAuth,
  getApplaussAccessToken,
  getApplaussAuth,
  clearApplaussAuth,
  refreshApplaussToken,
  needsApplaussTokenRefresh,
  getAnonymousHeaders,
  peekDeviceId,
  updateApplaussUser,
} from './authManager';
import { getBackendBaseUrl } from './backendUrl';
import i18n from '../i18n/config';
import { ANY_AUDIENCE_WIRE, normalizeAudienceIds, normalizeFilterIds } from './recommendationFilterOptions';
import { DEFAULT_RECOMMENDATION_LIMIT, MAX_RECOMMENDATION_LIMIT } from './recommendationFilters';
import { locationRequestParams, resolveUserLocation } from './userLocation';

const API_BASE_URL = getBackendBaseUrl();

// Only endpoints that must never see a token (they mint or exchange one) plus
// public config reads.
//
// Optional-auth endpoints (/recommendations, /search, /events/{id}, .../similar)
// must NOT be listed: they work anonymously, but a token gets them the caller's
// `user_interaction`. Listing `/events/` is what made a liked event come back
// unliked after a reload — the response was always the anonymous one.
// Shared links still work logged-out: getApplaussAccessToken returns null once
// expired, and a failed refresh continues unauthenticated.
const PUBLIC_PATHS = [
  '/auth/register',
  '/auth/login/email',
  '/auth/check-email',
  '/auth/refresh',
  '/cities',
];

// Create axios instance for Applauss API.
//
// Every request carries the UI language as `Accept-Language` (#673).
//
// Anonymous-tracking headers are always attached first. The backend
// dependency short-circuits on a valid JWT, so sending these for authed
// users is harmless — the dependency drops them before any DB write.
//
// Token refresh is proactive (request-side, on expiry) and single-flight via
// refreshApplaussToken. If refresh fails, the request continues WITHOUT auth
// so shared links keep working with expired tokens.
const applaussApi = createApiClient({
  baseURL: API_BASE_URL,
  // The UI language, not the browser's: the API writes descriptions and curator
  // sentences in it, and they have to match the copy around them.
  getLanguage: () => i18n.language,
  onRequest: (config) => {
    Object.assign(config.headers, getAnonymousHeaders());
  },
  isPublicPath: (url) => PUBLIC_PATHS.some((path) => url.includes(path)),
  getAccessToken: getApplaussAccessToken,
  proactiveRefresh: {
    shouldRefresh: needsApplaussTokenRefresh,
    execute: async () => (await refreshApplaussToken()).access_token,
    onFailure: () => {
      // Clear expired tokens and continue without auth so shared links work
      console.warn('⚠️ Token refresh failed, clearing expired tokens and continuing without auth');
      clearApplaussAuth();
      // Notify the app that authentication state has changed
      window.dispatchEvent(new CustomEvent('authChanged'));
    },
  },
});

/**
 * Transform backend recommendations to frontend searchHistory format
 * @param {Array} recommendations - Array from backend recommendation_history
 * @returns {Array} Transformed search history for frontend
 */
export function transformSearchHistory(recommendations) {
  if (!recommendations || !Array.isArray(recommendations)) {
    return [];
  }

  return recommendations.map(rec => ({
    id: rec.id,
    city: rec.filters?.city || 'Unknown',
    start_date: rec.filters?.start_date || null,
    end_date: rec.filters?.end_date || null,
    // The API splits the row's one column back into desires and formats (#443); only desires are read.
    ...normalizeFilterIds(rec.filters || {}),
    // A row without an audience is read as the default (#717): rows from before the default
    // existed carry none, and cannot be told from an Any request, so both re-apply the default
    // and both show no audience label — the same as a row that recorded the default itself.
    audience: normalizeAudienceIds(rec.filters?.audience),
    timestamp: rec.timestamp,
    total_results: rec.total_results
  }));
}

/**
 * Get recommendation history from backend
 * @param {number} limit - Number of recent recommendations to fetch (default: 5)
 * @returns {Promise<Array>} Recommendation history
 */
export async function getRecommendationHistory(limit = 5) {
  try {
    const response = await applaussApi.get('/auth/recommendation_history', {
      params: { limit }
    });

    console.log('✅ Recommendation history fetched:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get recommendation history:', error.response?.data || error.message);
    // Return empty recommendations if request fails
    return { user_id: null, recommendations: [] };
  }
}

/**
 * Get active cities configured for event discovery.
 * @returns {Promise<Array<{name: string, country?: string, image_url?: string}>>} City options from the backend configuration table
 */
export async function getAvailableCities() {
  try {
    const response = await applaussApi.get('/cities');
    return response.data?.cities ?? [];
  } catch (error) {
    console.error('❌ Failed to get available cities:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get the caller's onboarding profile.
 * Uses JWT when available, otherwise the anonymous X-Device-Id header injected
 * by the request interceptor.
 * @returns {Promise<{identity_type: string, profile: Object}>}
 */
export async function getOnboardingProfile() {
  try {
    const response = await applaussApi.get('/onboarding/profile');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get onboarding profile:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Save one onboarding step.
 * @param {'location'|'intents'|'themes'|'scenes'} stepId - Onboarding step identifier
 * @param {Object} payload - Step-specific payload
 * @returns {Promise<{status: string, identity_type: string, updated_step: string, profile: Object}>}
 */
export async function saveOnboardingStep(stepId, payload) {
  try {
    const response = await applaussApi.put(`/onboarding/steps/${encodeURIComponent(stepId)}`, payload);
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to save onboarding step ${stepId}:`, error.response?.data || error.message);
    throw error;
  }
}

/**
 * Mark onboarding complete for the caller.
 * @returns {Promise<{status: string, identity_type: string, profile: Object}>}
 */
export async function completeOnboarding() {
  try {
    const response = await applaussApi.post('/onboarding/complete');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to complete onboarding:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Search canonical onboarding themes in the backend themes index.
 * @param {string} query - Search query
 * @param {number} limit - Max number of themes
 * @returns {Promise<{query: string, total: number, themes: Array<{id: string, name: string}>}>}
 */
export async function searchOnboardingThemes(query, limit = 10) {
  const cleaned = String(query || '').trim();
  if (cleaned.length < 2) {
    return { query: cleaned, total: 0, themes: [] };
  }

  try {
    const response = await applaussApi.get('/onboarding/themes/search', {
      params: { query: cleaned, limit }
    });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to search onboarding themes:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get curated onboarding artist suggestions. Each is fully pre-resolved (mbid,
 * name, country, tags) so adding it needs no client-side MusicBrainz call.
 * @param {string} [city] - Optional city; local acts are listed first.
 * @param {number} [limit] - Max suggestions to return.
 * @returns {Promise<{total: number, artists: Array<{mbid: string, name: string, country: string|null, tags: string[]}>}>}
 */
export async function getSuggestedArtists(city, limit = 12) {
  try {
    const response = await applaussApi.get('/onboarding/suggested-artists', {
      params: { ...(city ? { city } : {}), limit }
    });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to load suggested artists:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get curated onboarding theme suggestions ({id, name}) from the backend.
 * @param {number} [limit] - Max suggestions to return.
 * @returns {Promise<{total: number, themes: Array<{id: string|null, name: string}>}>}
 */
export async function getSuggestedThemes(limit = 12) {
  try {
    const response = await applaussApi.get('/onboarding/suggested-themes', {
      params: { limit }
    });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to load suggested themes:', error.response?.data || error.message);
    throw error;
  }
}

// Note: refreshApplaussToken() is centralized in authManager.js (single-flight
// via @applauss/api-client). This eliminates the race condition that occurred
// when both authValidator.js and backendApi.js had their own refresh
// implementations.

/**
 * Send Spotify authentication tokens to the main backend
 * @param {Object} tokenInfo - Token information from Spotify
 * @param {string} tokenInfo.access_token - Spotify access token
 * @param {string} tokenInfo.refresh_token - Spotify refresh token
 * @param {number} tokenInfo.expires_in - Token expiry time in seconds
 * @param {number} topLimit - Limit for top items (default: 20)
 * @returns {Promise} Response from backend API
 */
export async function syncSpotifyTokens(tokenInfo, topLimit = 20) {
  try {
    // Calculate expires_at timestamp (current time + expires_in seconds)
    const expiresAt = Math.floor(Date.now() / 1000) + tokenInfo.expires_in;

    const payload = {
      token_info: {
        access_token: tokenInfo.access_token,
        refresh_token: tokenInfo.refresh_token,
        expires_at: expiresAt
      },
      top_limit: topLimit
    };

    const response = await applaussApi.post('/auth/login/spotify', payload);

    // Save Applauss authentication to localStorage if successful
    if (response.data.status === 'success') {
      // Process Spotify user data - extract picture from images array if needed
      const userData = response.data.user;
      const userPicture = userData.picture || (userData.images && userData.images[0]?.url);

      // Merge with picture URL
      const authDataWithPicture = {
        ...response.data,
        user: {
          ...userData,
          picture: userPicture
        }
      };

      console.log('🎵 [Spotify] Processed user data:', {
        display_name: authDataWithPicture.user.display_name,
        email: authDataWithPicture.user.email,
        picture: authDataWithPicture.user.picture
      });

      saveApplaussAuth(authDataWithPicture, 'spotify');
    }

    return response.data;
  } catch (error) {
    console.error('❌ Failed to sync tokens with backend:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Check if the backend API is reachable
 * @returns {Promise<boolean>}
 */
export async function checkBackendHealth() {
  try {
    const response = await applaussApi.get('/health', {
      timeout: 5000
    });
    return response.status === 200;
  } catch (error) {
    console.warn('⚠️ Backend API health check failed:', error.message);
    return false;
  }
}

/**
 * Make authenticated request to Applauss API
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} endpoint - API endpoint (e.g., '/users/me')
 * @param {Object} data - Request payload (for POST/PUT)
 * @returns {Promise} Response from backend API
 */
export async function applaussApiRequest(method, endpoint, data = null) {
  try {
    const config = {
      method: method.toLowerCase(),
      url: endpoint,
    };

    if (data && ['post', 'put', 'patch'].includes(method.toLowerCase())) {
      config.data = data;
    }

    const response = await applaussApi(config);
    return response.data;
  } catch (error) {
    console.error(`❌ Applauss API ${method} ${endpoint} failed:`, error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get event recommendations from Applauss API
 * @param {string} city - City name for recommendations
 * @param {Object} options - Optional filters
 * @param {string} options.start_date - Start date filter (YYYY-MM-DD, optional)
 * @param {string} options.end_date - End date filter (YYYY-MM-DD, optional)
 * @param {Array<string>} options.intents - Desire filters (optional)
 * @param {Array<string>} options.languages - Array of language filters (optional)
 * @param {Array<string>} options.audience - Children suitability filters (optional)
 * @param {number} options.limit - Max number of events to return (optional, capped at 500)
 * @param {Object|null} options.userLocation - Location to send instead of the stored one (optional)
 * @returns {Promise} Event recommendations
 */
/**
 * The filter params the feed sends, split out from the venue-classification ones so the
 * two groups stay legible as they grow.
 */
function buildEventFilterParams(options = {}) {
  const params = {};

  if (options.start_date) params.start_date = options.start_date;
  if (options.end_date) params.end_date = options.end_date;
  if (options.domain) params.domain = options.domain;
  if (options.free_only) params.free_only = true;

  // Already canonical: the filters came through `normalizeFilterIds` at the entry point.
  const { intents = [] } = options;
  // Options without an audience ask for none; the default was applied when they were built. Any
  // is sent as every value (ANY_AUDIENCE_WIRE), so its request is not mistaken for no choice.
  const audience = normalizeAudienceIds(options.audience ?? []);

  if (intents.length > 0) params.intents = intents.join(',');
  if (audience.length > 0) params.audience = audience.join(',');
  else if (options.audience != null) params.audience = ANY_AUDIENCE_WIRE.join(',');
  if (options.languages && options.languages.length > 0) params.languages = options.languages.join(',');

  return params;
}

export async function getRecommendations(city, options = {}) {
  try {
    const requestedLimit = Number(options.limit);
    const params = {
      city,
      limit: Number.isFinite(requestedLimit) && requestedLimit > 0
        // Clamped to the API's own ceiling, not to the feed's default: the map asks for
        // a whole day, which is more than the feed ever shows.
        ? Math.min(requestedLimit, MAX_RECOMMENDATION_LIMIT)
        : DEFAULT_RECOMMENDATION_LIMIT,
      // The stored location is read here when the caller does not hold one: the OAuth
      // prefetch and onboarding's two fetches have never heard of a location, and a
      // returning user should still get distance ranking on the first of them, not only
      // after touching a filter. `city` is passed so a location stored for another city is
      // dropped here rather than by each of them.
      //
      // `options.userLocation` overrides storage for a caller that holds the live location
      // (the recommendations page's two fetches and the map's two, #595): what the pill
      // shows and what is sent are then one value, even where a browser refusing storage
      // keeps nothing. `null` is "none". Named for what it is rather than `location`, which
      // a filter could claim.
      ...locationRequestParams(resolveUserLocation(options.userLocation), city),
      ...buildEventFilterParams(options)
    };

    // Only sent when opting out, so every other caller's request is unchanged and the
    // param stays absent from the common case. The map passes it: a curated feed is ~32
    // of the ~100 ranked events, which on a map reads as a filter nobody applied.
    if (options.curate === false) {
      params.curate = false;
    }

    // Same shape, same reason: absent unless a caller opts out. A request that is not a
    // ranked list somebody scrolled is not an impression, and the API logs one row per
    // returned event into the dataset the offline ranker evaluation reads. The map passes
    // it — a day per tap of its date strip, uncurated and up to the ceiling, in an order
    // that means nothing on a map.
    if (options.log_impressions === false) {
      params.log_impressions = false;
    }

    // Venue-classification filters.
    if (options.venue_size) {
      params.venue_size = options.venue_size;
    }
    if (options.venue_types && options.venue_types.length > 0) {
      params.venue_type_hint = options.venue_types.join(',');
    }
    if (options.venue_modality) {
      params.venue_modality = options.venue_modality;
    }

    console.log('📤 Sending to /recommendations:', params);

    const response = await applaussApi.get('/recommendations', {
      params
    });


    return response.data;
  } catch (error) {
    console.error('❌ Failed to get recommendations:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Geocode a typed place or address into coordinates for the located feed.
 *
 * The backend caps itself at one lookup per second (OpenStreetMap's usage policy), so
 * callers must debounce rather than search on every keystroke. An unreachable geocoder
 * comes back as an empty list, not an error — the picker degrades, the page does not.
 *
 * @param {string} query - What the user typed
 * @param {Object} options - `city` scopes results to that city's radius; `limit` caps them
 * @returns {Promise<Array<{label: string, lat: number, lon: number}>>}
 */
export async function searchLocations(query, { city = null, limit = 5 } = {}) {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  try {
    // `city` limits results to that city's discovery radius: a street name that repeats
    // worldwide would otherwise let the user rank the feed around a point 500 km away.
    const response = await applaussApi.get('/locations/search', {
      params: city ? { q, city, limit } : { q, limit }
    });
    return response.data?.results || [];
  } catch (error) {
    console.error('❌ Location search failed:', error.response?.data || error.message);
    return [];
  }
}

/**
 * Get events similar to a given event (for "You might also like" sections).
 * City scoping is server-side (derived from the event's own venue) — no city arg.
 * @param {string} eventId - The reference event ID
 * @param {Object} options - Optional filters (start_date, end_date, domain, limit)
 * @returns {Promise} Similar events response
 */
export async function getSimilarEvents(eventId, options = {}) {
  try {
    const params = { limit: options.limit || 6 };
    if (options.start_date) params.start_date = options.start_date;
    if (options.end_date) params.end_date = options.end_date;
    if (options.domain) params.domain = options.domain;

    const response = await applaussApi.get(`/events/${eventId}/similar`, { params });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get similar events:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Search events by query (artist, event name, venue)
 * @param {string} query - Search query
 * @param {object} options - Optional filters (city, intents, audience, free_only, start_date, end_date, limit, languages, venue_modality)
 * @returns {Promise} Search results
 */
export async function searchEvents(query, options = {}) {
  try {
    // The feed's own filter builder, so search sends the same normalized ids.
    const params = { query, ...buildEventFilterParams(options) };

    if (options.city) params.city = options.city;
    if (options.venue_modality) params.venue_modality = options.venue_modality;
    if (options.limit) params.limit = options.limit;

    const response = await applaussApi.get('/search', { params });
    return response.data;
  } catch (error) {
    console.error('❌ Failed to search events:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get a specific event by ID
 * @param {string} eventId - Event ID
 * @returns {Promise} Event data
 */
export async function getEventById(eventId) {
  try {
    // Interceptor handles authentication automatically (optional for this endpoint)
    const response = await applaussApi.get(`/events/${eventId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get event:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Buy one LLM explanation for one event, because the reader asked for it.
 *
 * Only reachable while the curator is in its on-demand strategy — the feed response says
 * so in `explain_on_demand`, which is why a caller should not fire this speculatively:
 * every first press for a given reader and event spends money, and the server refuses
 * with 409 rather than 403 when the strategy is off or the day's budget is gone.
 *
 * Returns the same `recommendation_explanation` object a curated feed already ships, so
 * the caller drops it onto the event it is holding and every surface that already renders
 * a curator pick renders this one too.
 *
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} The `recommendation_explanation` object
 */
export async function explainEvent(eventId) {
  const response = await applaussApi.post(`/events/${eventId}/explain`);
  return response.data?.recommendation_explanation ?? null;
}

/**
 * Get full occurrence detail for a specific event date (lazy-calendar day tap)
 * @param {string} eventId - Event ID
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {Promise<{event_id: string, date: string, occurrences: Array}>}
 */
export async function getEventOccurrencesForDate(eventId, date) {
  const response = await applaussApi.get(`/events/${eventId}/occurrences`, {
    params: { date },
  });
  return response.data;
}

/**
 * Get liked events from Applauss API
 * @returns {Promise} Liked events (similar to recommendations)
 */
export async function getLikedEvents() {
  try {
    const response = await applaussApi.get('/liked-events');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get liked events:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get disliked events from Applauss API
 * @returns {Promise} Disliked events (similar to recommendations)
 */
export async function getDislikedEvents() {
  try {
    const response = await applaussApi.get('/disliked-events');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get disliked events:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Create event interaction (like/dislike)
 * @param {string} eventId - Event ID
 * @param {string} interactionType - 'like' or 'dislike'
 * @returns {Promise} Interaction response
 */
export async function createInteraction(eventId, interactionType) {
  try {
    console.log('📤 Creating interaction:', { event_id: eventId, interaction_type: interactionType });
    const response = await applaussApi.post('/interactions', {
      event_id: eventId,
      interaction_type: interactionType
    });
    console.log('✅ Interaction response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to create interaction:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * An API error rethrown for a caller that reads `status`: the original axios error rides along as
 * `originalError`, for `errorMessage()` to read its code. Never the API's text as the message (#683).
 */
function wrapApiError(error) {
  const wrapped = new Error(error.message);
  wrapped.status = error.response?.status;
  wrapped.originalError = error;
  return wrapped;
}

/**
 * Register new user with email and password
 * @param {Object} data - { email, password, display_name }
 * @returns {Promise} User data with tokens
 */
export async function registerWithEmail(data) {
  try {
    // Capture device_id BEFORE saveApplaussAuth rotates it, so the backend
    // can merge any anonymous activity into the freshly-created user.
    const deviceId = peekDeviceId();
    const payload = deviceId ? { ...data, device_id: deviceId } : data;
    const response = await applaussApi.post('/auth/register', payload);

    // Save tokens and user data
    if (response.data.access_token && response.data.refresh_token) {
      saveApplaussAuth({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_in: response.data.expires_in || 3600,
        user: response.data.user
      }, 'email');
    }

    return response.data;
  } catch (error) {
    console.error('❌ Registration failed:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Login user with email and password
 * @param {Object} data - { email, password }
 * @returns {Promise} User data with tokens
 */
export async function loginWithEmail(data) {
  try {
    const deviceId = peekDeviceId();
    const payload = deviceId ? { ...data, device_id: deviceId } : data;
    const response = await applaussApi.post('/auth/login/email', payload);

    // Save tokens and user data
    if (response.data.access_token && response.data.refresh_token) {
      saveApplaussAuth({
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token,
        expires_in: response.data.expires_in || 3600,
        user: response.data.user
      }, 'email');
    }

    return response.data;
  } catch (error) {
    console.error('❌ Login failed:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Merge an anonymous device's history into the authenticated user.
 *
 * Called by OAuthHandler after Google/Spotify success, since their request
 * bodies are server-controlled and can't carry device_id. Pass the device_id
 * captured BEFORE ``saveApplaussAuth`` — that helper rotates it as part of
 * the login transition. Failures are non-fatal — never block the user on a
 * merge hiccup.
 *
 * @param {string} deviceId - The captured anonymous device_id.
 * @returns {Promise<{merged: boolean, interactions_migrated?: number, reason?: string} | null>}
 */
export async function claimAnonymous(deviceId) {
  const id = deviceId || peekDeviceId();
  if (!id) {
    return null;
  }
  try {
    const response = await applaussApi.post('/auth/claim-anonymous', { device_id: id });
    return response.data;
  } catch (error) {
    console.warn('⚠️ claim-anonymous failed (non-fatal):', error.response?.data || error.message);
    return null;
  }
}

const PURGE_TIMEOUT_MS = 8000;

/**
 * Erase the anonymous profile the backend holds for a device_id.
 *
 * The server-side half of withdrawing consent on /cookies. Pass the device_id
 * captured with ``peekDeviceId`` BEFORE ``setAnonymousConsent('declined')`` —
 * that helper rotates the identifier out of the browser, and the header
 * interceptor stops sending it the moment consent is gone, so the id is sent
 * explicitly here rather than left to ``getAnonymousHeaders``.
 *
 * Best-effort: a failed purge must never block the local withdrawal from
 * taking effect, so this resolves to null instead of throwing.
 *
 * @param {string} deviceId - The device_id to erase.
 * @returns {Promise<{purged: boolean, sessions_deleted?: number, profile_deleted?: boolean, reason?: string} | null>}
 */
export async function purgeAnonymousProfile(deviceId) {
  const id = deviceId || peekDeviceId();
  if (!id) {
    return null;
  }
  try {
    const response = await applaussApi.delete('/anonymous/profile', {
      headers: { 'X-Device-Id': id },
      // The caller awaits this before withdrawing locally, and the shared
      // client sets no default timeout — without one, an unreachable API would
      // hang a legally-required control indefinitely. On timeout this rejects,
      // which is handled below and lets the local withdrawal proceed.
      timeout: PURGE_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    console.warn('⚠️ anonymous profile purge failed (non-fatal):', error.response?.data || error.message);
    return null;
  }
}

/**
 * Check if email exists in the system
 * @param {string} email - Email address to check
 * @returns {Promise} Object with exists flag, email, auth_methods, and has_password
 */
export async function checkEmail(email) {
  try {
    const response = await applaussApi.post('/auth/check-email', { email });
    return response.data;
  } catch (error) {
    console.error('❌ Email check failed:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Initiate Google OAuth login
 * Redirects to backend Google OAuth endpoint
 */
export function initiateGoogleLogin() {
  const backendUrl = getBackendBaseUrl();
  window.location.href = `${backendUrl}/oauth/google/login`;
}

/**
 * Initiate Facebook OAuth login
 * Redirects to backend Facebook OAuth endpoint
 */
export function initiateFacebookLogin() {
  const backendUrl = getBackendBaseUrl();
  window.location.href = `${backendUrl}/auth/facebook/login`;
}

/**
 * Manually refresh Applauss token (exported for external use)
 * @returns {Promise<Object>} Refresh result with access_token, user, and success flag
 */
export async function refreshToken() {
  return await refreshApplaussToken();
}

/**
 * Change user password (only for email/password accounts)
 * @param {Object} data - { currentPassword, newPassword }
 * @returns {Promise} Response
 */
export async function changePassword(data) {
  try {
    const response = await applaussApi.post('/auth/change-password', data);
    return response.data;
  } catch (error) {
    console.error('❌ Password change failed:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Permanently delete the authenticated user's account (Postgres + Elasticsearch).
 *
 * @returns {Promise<{deleted: boolean, user_id: string, email: string|null, message: string}>}
 */
export async function deleteAccount() {
  try {
    const response = await applaussApi.delete('/auth/delete');
    return response.data;
  } catch (error) {
    console.error('❌ Account deletion failed:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Set or clear the user's display name.
 *
 * @param {string|null} displayName - New name; null or blank clears it
 * @returns {Promise<string|null>} The name as stored (trimmed, or null)
 */
export async function updateDisplayName(displayName) {
  const response = await applaussApi.patch('/user/profile', { display_name: displayName ?? null });
  const stored = response.data?.display_name ?? null;
  updateApplaussUser({ display_name: stored });
  return stored;
}

/**
 * Link additional OAuth provider to account
 * @param {string} provider - Provider to link ('google' or 'facebook')
 * @returns {void} Redirects to OAuth flow
 */
export function linkOAuthProvider(provider) {
  const backendUrl = getBackendBaseUrl();
  window.location.href = `${backendUrl}/auth/${provider}/link`;
}

/**
 * Unlink OAuth provider from account
 * @param {string} provider - Provider to unlink ('google' or 'facebook')
 * @returns {Promise} Response
 */
export async function unlinkOAuthProvider(provider) {
  try {
    const response = await applaussApi.post(`/auth/${provider}/unlink`);
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to unlink ${provider}:`, error.response?.data || error.message);
    throw error;
  }
}

/**
 * Get user's favorite artists
 * @returns {Promise<Object>} Response with artists array
 * @throws {Error} If request fails
 */
export async function getFavoriteArtists() {
  try {
    // Debug: Check auth token and user info
    const auth = getApplaussAuth();
    console.log('🔍 Auth check for getFavoriteArtists:', {
      hasAuth: !!auth,
      userId: auth?.user?.id,
      userEmail: auth?.user?.email,
      isExpired: auth?.isExpired
    });

    const response = await applaussApi.get('/users/artists');
    console.log('🔍 GET /users/artists response:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to get favorite artists:', error.message);
    console.error('❌ Full error response:', error.response?.data);
    throw wrapApiError(error);
  }
}

/**
 * Add artist to user's favorites
 * @param {Object} artistData - Complete artist data from MusicBrainz
 * @returns {Promise<Object>} Response with created artist
 * @throws {Error} If request fails (400, 409, 401, 500)
 */
export async function addFavoriteArtist(artistData) {
  try {
    // Debug: Check auth and request data
    const auth = getApplaussAuth();
    console.log('🔍 Auth check for addFavoriteArtist:', {
      hasAuth: !!auth,
      userId: auth?.user?.id,
      userEmail: auth?.user?.email,
      isExpired: auth?.isExpired
    });
    console.log('🔍 POST /users/artists request data:', {
      mbid: artistData.id,
      name: artistData.name,
      type: artistData.type,
      hasFullData: !!artistData
    });

    const response = await applaussApi.post('/users/artists', artistData);
    console.log('✅ Artist added to favorites');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to add favorite artist:', error.response?.data || error.message);
    throw wrapApiError(error);
  }
}

/**
 * Add multiple artists to the user's favorites in a single request.
 *
 * Used by onboarding to persist the whole artist selection at once instead of
 * one POST per artist. Already-favorited artists are skipped server-side (not an
 * error), so this never rejects with a 409 for duplicates.
 *
 * @param {Object[]} artists - Complete artist data objects from MusicBrainz
 *   (each requires `id` and `name`; pass `source_detail: 'onboarding'` to tag origin)
 * @returns {Promise<Object>} Response with { added, skipped, errored }
 * @throws {Error} If the request fails (400, 401, 500)
 */
export async function addFavoriteArtists(artists) {
  try {
    const response = await applaussApi.post('/users/artists/batch', { artists });
    console.log(`✅ ${artists.length} artist(s) sent to favorites (batch)`);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to add favorite artists (batch):', error.response?.data || error.message);
    throw wrapApiError(error);
  }
}

/**
 * Add an artist that already exists in the Applauss catalogue to the user's favorites.
 *
 * Use this whenever the caller already holds an Applauss artist id — an event's
 * performer refs, for instance. `addFavoriteArtist` posts raw MusicBrainz data and
 * the backend reads its `id` as an MBID, so sending an internal id there creates a
 * duplicate artist record instead of favoriting the one the user pointed at.
 *
 * @param {string} artistId - Internal Applauss artist ID (or a MusicBrainz MBID)
 * @returns {Promise<Object>} Response with the stored artist ref
 * @throws {Error} If the request fails (401, 404, 409, 500)
 */
export async function addFavoriteArtistById(artistId) {
  try {
    const response = await applaussApi.post(`/users/artists/${artistId}`);
    console.log('✅ Artist added to favorites');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to add favorite artist by id:', error.response?.data || error.message);
    throw wrapApiError(error);
  }
}

/**
 * Remove artist from user's favorites
 * @param {string} artistId - Artist ID (MBID or backend ID)
 * @returns {Promise<Object>} Response
 * @throws {Error} If request fails (404, 401, 500)
 */
export async function removeFavoriteArtist(artistId) {
  try {
    const response = await applaussApi.delete(`/users/artists/${artistId}`);
    console.log('✅ Artist removed from favorites');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to remove favorite artist:', error.response?.data || error.message);
    throw wrapApiError(error);
  }
}

/**
 * Remove multiple artists from the user's favorites in a single request.
 *
 * Used by onboarding to drop all de-selected artists at once instead of one
 * DELETE per artist (each triggers a profile genres/embedding resync). Ids not
 * on the profile are returned in `not_found` (not an error), so this never
 * rejects with a 404 for an already-removed artist.
 *
 * @param {string[]} artistIds - Artist ids (internal id or MusicBrainz id)
 * @returns {Promise<Object>} Response with { removed, not_found }
 * @throws {Error} If the request fails (400, 401, 500)
 */
export async function removeFavoriteArtists(artistIds) {
  try {
    const response = await applaussApi.post('/users/artists/batch-remove', { artist_ids: artistIds });
    console.log(`✅ ${artistIds.length} artist(s) removed from favorites (batch)`);
    return response.data;
  } catch (error) {
    console.error('❌ Failed to remove favorite artists (batch):', error.response?.data || error.message);
    throw wrapApiError(error);
  }
}

/**
 * Request password reset - sends email with reset link
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Response with success message
 */
export async function requestPasswordReset(email) {
  try {
    const response = await applaussApi.post('/auth/request-password-reset', { email });
    console.log('✅ Password reset email sent');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to request password reset:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Reset password with token
 * @param {string} token - Reset token from email
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response with success message and user_id
 */
export async function resetPassword(token, newPassword) {
  try {
    const response = await applaussApi.post('/auth/reset-password', {
      token,
      password: newPassword
    });
    console.log('✅ Password reset successfully');
    return response.data;
  } catch (error) {
    console.error('❌ Failed to reset password:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Record a referral link open
 * @param {string} eventId - Event ID opened via referral link
 * @param {string} referrerId - User ID of the person who shared the link
 * @returns {Promise<Object|null>} Referral response, or null if already recorded (409)
 */
export async function createReferral(eventId, referrerId) {
  try {
    const response = await applaussApi.post('/referrals', {
      event_id: eventId,
      referrer_id: referrerId
    });
    console.log('✅ Referral recorded');
    return response.data;
  } catch (error) {
    if (error.response?.status === 409) return null; // Already recorded
    console.error('❌ Failed to record referral:', error.response?.data || error.message);
    throw error;
  }
}

/**
 * Report an event issue
 * @param {string} eventId - Event ID to report
 * @param {string} reason - Report reason enum value
 * @param {string} [description] - Optional details
 * @returns {Promise<Object>} Report response
 */
export async function reportEvent(eventId, reason, description) {
  const body = {
    entity_type: 'event',
    entity_id: eventId,
    reason
  };
  if (description) body.description = description;

  const response = await applaussApi.post('/reports', body);
  return response.data;
}

/**
 * Poll the background enrichment status for the authenticated user.
 * The user identity is derived from the JWT by the backend — no user_id param needed.
 * @returns {Promise<{status: 'running'|'done'|'error'|'not_found'}>}
 */
export async function getEnrichmentStatus() {
  const response = await applaussApi.get('/auth/enrichment-status');
  return response.data;
}

/**
 * Fetch which login methods are enabled (public, unauthenticated).
 * Admins toggle these from the admin dashboard. Falls back to all-enabled on
 * error so a backend hiccup never blocks users from logging in.
 * @returns {Promise<{email: boolean, spotify: boolean, google: boolean}>}
 */
export async function getEnabledLoginMethods() {
  try {
    const response = await applaussApi.get('/auth/login-methods');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch login methods, defaulting to all enabled:', error?.message || error);
    return { email: true, spotify: true, google: true };
  }
}

// Export the axios instance for advanced usage
export { applaussApi };
